import React from 'react';
import { useTranslation } from 'react-i18next';
import { OrphanedFilesResult } from '../../../../api/General/blobMigration';
import AppIcon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

interface OrphanedFilesPanelProps {
  orphanedResult: OrphanedFilesResult | null;
  loadingOrphaned: boolean;
  onLoad: () => void;
}

export default function OrphanedFilesPanel({
  orphanedResult,
  loadingOrphaned,
  onLoad,
}: OrphanedFilesPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-6 py-4">
        <h3 className="text-lg font-semibold text-foreground">{t('Orphaned Files')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('Detect DB records pointing to missing files and disk files not tracked in the database')}
        </p>
      </div>
      <div className="p-6 flex flex-col gap-6">
        <Button
          variant="outline"
          onClick={onLoad}
          disabled={loadingOrphaned}
          loading={loadingOrphaned}
        >
          {loadingOrphaned ? t('Loading...') : t('Load Orphaned Files')}
        </Button>

        {orphanedResult ? (
          <div className="flex flex-col gap-6">
            {/* Orphaned DB References */}
            <div>
              <div className="mb-1 flex items-center gap-1.5">
                <AppIcon name="XCircle" size={16} className="text-destructive" />
                <span className="text-sm font-medium text-foreground">
                  {t('Orphaned DB References')} ({orphanedResult.orphanedDbReferences.length})
                </span>
              </div>
              <p className="mb-2 text-xs text-muted-foreground">
                {t('Database records pointing to files that no longer exist on disk')}
              </p>
              {orphanedResult.orphanedDbReferences.length > 0 ? (
                <div className="max-h-[300px] overflow-auto rounded-md border border-border">
                  <ul className="divide-y divide-border">
                    {orphanedResult.orphanedDbReferences.map((ref, index) => (
                      <li key={index} className="px-4 py-2">
                        <p className="text-sm text-destructive">{ref.filePath}</p>
                        <p className="text-xs text-muted-foreground">
                          {t('Category')}: {t(ref.category)} | {t('Entity ID')}: {ref.entityId}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200">
                  <AppIcon name="CheckCircle2" size={16} />
                  {t('No orphaned DB references found')}
                </div>
              )}
            </div>

            {/* Orphaned Disk Files */}
            <div>
              <div className="mb-1 flex items-center gap-1.5">
                <AppIcon name="AlertTriangle" size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">
                  {t('Orphaned Disk Files')} ({orphanedResult.orphanedDiskFiles.length})
                </span>
              </div>
              <p className="mb-2 text-xs text-muted-foreground">
                {t('Files on disk that are not referenced by any database record')}
              </p>
              {orphanedResult.orphanedDiskFiles.length > 0 ? (
                <div className="max-h-[300px] overflow-auto rounded-md border border-border">
                  <ul className="divide-y divide-border">
                    {orphanedResult.orphanedDiskFiles.map((file, index) => (
                      <li key={index} className="px-4 py-2 text-sm text-foreground">
                        {file}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 dark:border-green-800 dark:bg-green-900/30 dark:text-green-200">
                  <AppIcon name="CheckCircle2" size={16} />
                  {t('No orphaned disk files found')}
                </div>
              )}
            </div>
          </div>
        ) : !loadingOrphaned ? (
          <p className="text-sm text-muted-foreground">
            {t('Click the button above to scan for orphaned files')}
          </p>
        ) : null}
      </div>
    </div>
  );
}
